import { useTranslation } from 'react-i18next';
import { FiUser, FiUsers, FiMoreHorizontal } from 'react-icons/fi';
import { HiOutlineOfficeBuilding, HiOutlineHome } from 'react-icons/hi';
import {
  CLIENTS_OR_NETWORKS_RANGES,
  FRANCHISE_UNITS_RANGES,
  OPERATION_OPTIONS,
  RANGE_KEYS,
  UI_SCREENS,
} from '../../../../constants/onboardingOperation';
import { Heading } from '../../Onboarding/styles';
import {
  Root,
  Badge,
  BadgeIcon,
  BadgeText,
  Subtitle,
  OperationGrid,
  ScopeGrid,
  ScaleRow,
  SelectRadio,
  OperationOption,
  OptionIcon,
  OptionCopy,
  OptionTitle,
  OptionDescription,
  ScopeOption,
  ScopeIcon,
  ScaleOption,
  ScaleValue,
  ScaleUnit,
} from './styles';

const OPERATION_CARDS = [
  {
    key: OPERATION_OPTIONS.FEW_NETWORKS,
    tone: 'few',
    Icon: FiUser,
  },
  {
    key: OPERATION_OPTIONS.AGENCY,
    tone: 'agency',
    Icon: FiUsers,
  },
  {
    key: OPERATION_OPTIONS.FRANCHISE,
    tone: 'franchise',
    Icon: HiOutlineOfficeBuilding,
  },
  {
    key: OPERATION_OPTIONS.OTHER,
    tone: 'other',
    Icon: FiMoreHorizontal,
  },
];

const FRANCHISE_SCOPE_CARDS = [
  {
    key: 'one',
    tone: 'franchise',
    Icon: HiOutlineHome,
  },
  {
    key: 'several',
    tone: 'agency',
    Icon: HiOutlineOfficeBuilding,
  },
];

const RANGE_UNIT_BY_SCREEN = {
  [UI_SCREENS.AGENCY_SCALE]: 'client',
  [UI_SCREENS.FRANCHISE_SCALE]: 'unit',
  [UI_SCREENS.OTHER_SCALE]: 'network',
};

const BADGE_BY_SCREEN = {
  [UI_SCREENS.AGENCY_SCALE]: { tone: 'agency', labelKey: 'badge.agency', Icon: FiUsers },
  [UI_SCREENS.FRANCHISE_SCOPE]: {
    tone: 'franchise',
    labelKey: 'badge.franchise',
    Icon: HiOutlineOfficeBuilding,
  },
  [UI_SCREENS.FRANCHISE_SCALE]: {
    tone: 'franchise',
    labelKey: 'badge.franchise',
    Icon: HiOutlineOfficeBuilding,
  },
  [UI_SCREENS.OTHER_SCALE]: { tone: 'other', labelKey: 'badge.other', Icon: FiMoreHorizontal },
};

function unitKeyForRange(unit, rangeKey) {
  if (unit === 'unit') {
    return 'other';
  }

  return rangeKey === RANGE_KEYS.ONE ? 'one' : 'other';
}

function SelectedProfileBadge({ screenKey }) {
  const { t } = useTranslation();
  const config = BADGE_BY_SCREEN[screenKey];
  if (!config) {
    return null;
  }

  const { Icon, tone, labelKey } = config;
  const profile = t(`settings.steps.operation.${labelKey}`);
  // Sem escapeValue:false o i18next troca "/" por "&#x2F;" e o split(profile) falha → label duplicado.
  const rawLabel = t('settings.steps.operation.selectedProfile', {
    profile,
    interpolation: { escapeValue: false },
  });
  const label = rawLabel.includes('{{profile}}')
    ? rawLabel.replace('{{profile}}', profile)
    : rawLabel;
  const [before, after = ''] = label.split(profile);

  return (
    <Badge data-testid="selected-profile-badge">
      <BadgeIcon $tone={tone}>
        <Icon aria-hidden />
      </BadgeIcon>
      <BadgeText component="span" variant="body2">
        {before}
        <strong>{profile}</strong>
        {after}
      </BadgeText>
    </Badge>
  );
}

function ScaleOptions({ ranges, selectedKey, onSelect, unit }) {
  const { t } = useTranslation();

  return (
    <ScaleRow data-testid="scale-row">
      {ranges.map((rangeKey) => {
        const checked = rangeKey === selectedKey;
        return (
          <ScaleOption
            key={rangeKey}
            type="button"
            onClick={() => onSelect(rangeKey)}
            $checked={checked}
            data-testid={`range-${rangeKey}`}
            aria-pressed={checked}
          >
            <SelectRadio $checked={checked} aria-hidden />
            <ScaleValue component="span" variant="h4">
              {t(`settings.steps.operation.ranges.${rangeKey}`)}
            </ScaleValue>
            <ScaleUnit component="span" variant="body2">
              {t(`settings.steps.operation.rangeUnit.${unit}.${unitKeyForRange(unit, rangeKey)}`)}
            </ScaleUnit>
          </ScaleOption>
        );
      })}
    </ScaleRow>
  );
}

export default function OperationCard({ screenKey, selectedKey, onSelect }) {
  const { t } = useTranslation();

  const headings = {
    [UI_SCREENS.OPERATION]: t('settings.steps.operation.heading'),
    [UI_SCREENS.AGENCY_SCALE]: t('settings.steps.operation.agencyHeading'),
    [UI_SCREENS.FRANCHISE_SCOPE]: t('settings.steps.operation.franchiseScopeHeading'),
    [UI_SCREENS.FRANCHISE_SCALE]: t('settings.steps.operation.franchiseScaleHeading'),
    [UI_SCREENS.OTHER_SCALE]: t('settings.steps.operation.otherHeading'),
  };

  return (
    <Root>
      <SelectedProfileBadge screenKey={screenKey} />

      <Heading component="h2" variant="h4">
        {headings[screenKey]}
      </Heading>

      {screenKey === UI_SCREENS.AGENCY_SCALE && (
        <Subtitle component="p" variant="body2">
          {t('settings.steps.operation.agencySubtitle')}
        </Subtitle>
      )}

      {screenKey === UI_SCREENS.OPERATION && (
        <OperationGrid data-testid="operation-grid">
          {OPERATION_CARDS.map(({ key, tone, Icon }) => {
            const checked = key === selectedKey;
            return (
              <OperationOption
                key={key}
                type="button"
                onClick={() => onSelect(key)}
                $checked={checked}
                data-testid={`option-${key}`}
                aria-pressed={checked}
              >
                <OptionIcon $tone={tone}>
                  <Icon aria-hidden />
                </OptionIcon>
                <OptionCopy>
                  <OptionTitle component="span" variant="body1">
                    {t(`settings.steps.operation.options.${key}.title`)}
                  </OptionTitle>
                  <OptionDescription component="span" variant="body2">
                    {t(`settings.steps.operation.options.${key}.description`)}
                  </OptionDescription>
                </OptionCopy>
                <SelectRadio $checked={checked} aria-hidden />
              </OperationOption>
            );
          })}
        </OperationGrid>
      )}

      {screenKey === UI_SCREENS.FRANCHISE_SCOPE && (
        <ScopeGrid data-testid="franchise-scope-grid">
          {FRANCHISE_SCOPE_CARDS.map(({ key, tone, Icon }) => {
            const checked = key === selectedKey;
            return (
              <ScopeOption
                key={key}
                type="button"
                onClick={() => onSelect(key)}
                $checked={checked}
                data-testid={`option-${key}`}
                aria-pressed={checked}
              >
                <SelectRadio $checked={checked} aria-hidden />
                <ScopeIcon $tone={tone}>
                  <Icon aria-hidden />
                </ScopeIcon>
                <OptionTitle component="span" variant="body1">
                  {t(`settings.steps.operation.franchiseScope.${key}.title`)}
                </OptionTitle>
              </ScopeOption>
            );
          })}
        </ScopeGrid>
      )}

      {screenKey === UI_SCREENS.AGENCY_SCALE && (
        <ScaleOptions
          ranges={CLIENTS_OR_NETWORKS_RANGES}
          selectedKey={selectedKey}
          onSelect={onSelect}
          unit={RANGE_UNIT_BY_SCREEN[UI_SCREENS.AGENCY_SCALE]}
        />
      )}

      {screenKey === UI_SCREENS.OTHER_SCALE && (
        <ScaleOptions
          ranges={CLIENTS_OR_NETWORKS_RANGES}
          selectedKey={selectedKey}
          onSelect={onSelect}
          unit={RANGE_UNIT_BY_SCREEN[UI_SCREENS.OTHER_SCALE]}
        />
      )}

      {screenKey === UI_SCREENS.FRANCHISE_SCALE && (
        <ScaleOptions
          ranges={FRANCHISE_UNITS_RANGES}
          selectedKey={selectedKey}
          onSelect={onSelect}
          unit={RANGE_UNIT_BY_SCREEN[UI_SCREENS.FRANCHISE_SCALE]}
        />
      )}
    </Root>
  );
}
